// app/frontend/controllers/loading_overlay_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "bar", "tag", "label"]
  static values  = { delay: Number, disableBar: Boolean }

  connect() {
    // Singleton for turbo-permanent
    if (window.__loadingOverlay__ && window.__loadingOverlay__ !== this) { this.element.remove(); return }
    window.__loadingOverlay__ = this

    // Ensure we live directly under <body> (avoids transformed ancestors blocking fixed children)
    if (this.element.parentElement !== document.body) document.body.appendChild(this.element)

    // Ensure container covers viewport & stacks on top
    const el = this.element
    el.style.position      = "fixed"
    el.style.inset         = "0"
    el.style.zIndex        = "2147483000"
    el.style.isolation     = "isolate"
    el.style.pointerEvents = "none" // toggled at show/hide

    // State
    this.delayMs           = Number.isFinite(this.delayValue) ? this.delayValue : 60
    this._timer            = null
    this._tick             = null
    this._progress         = 0
    this._showing          = false
    this._minShowUntil     = 0
    this._failsafe         = null
    this._navArmed         = false
    this._networkStarted   = false
    this._noNavAbortTimer  = null
    this._clickBound       = false
    this._submitBound      = false
    this._startTime        = 0
    this._showCenteredBar  = !this.disableBarValue
    this._shownPageToken   = null

    // Bind
    this._onStart             = this._onStart.bind(this)
    this._onEndAfterPaint     = this._onEndAfterPaint.bind(this)
    this._onPageShow          = this._onPageShow.bind(this)
    this._onPrimeLink         = this._onPrimeLink.bind(this)
    this._onPrimeForm         = this._onPrimeForm.bind(this)
    this._onVisibilityChange  = this._onVisibilityChange.bind(this)

    // Start signals (no hover/prefetch)
    document.addEventListener("turbo:visit",        this._onStart)
    document.addEventListener("turbo:submit-start", this._onStart)
    window.addEventListener("beforeunload",         this._onStart)
    window.addEventListener("pagehide",             this._onStart)

    // End signals → wait for the final page load instead of preview/frame renders
    document.addEventListener("turbo:load",                 this._onEndAfterPaint)
    document.addEventListener("turbo:fetch-request-error",  this._onEndAfterPaint)
    document.addEventListener("turbo:visit-error",          this._onEndAfterPaint)

    window.addEventListener("pageshow", this._onPageShow)
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    // Prime on real interactions (immediate visual feedback)
    if (!this._clickBound)  { document.addEventListener("click",  this._onPrimeLink, { capture: true }); this._clickBound  = true }
    if (!this._submitBound) { document.addEventListener("submit", this._onPrimeForm, { capture: true }); this._submitBound = true }

    this._normalizeBarLayout()
    this._resetVisualState()
  }

  disconnect() {
    document.removeEventListener("turbo:visit",        this._onStart)
    document.removeEventListener("turbo:submit-start", this._onStart)

    document.removeEventListener("turbo:load",                this._onEndAfterPaint)
    document.removeEventListener("turbo:fetch-request-error", this._onEndAfterPaint)
    document.removeEventListener("turbo:visit-error",         this._onEndAfterPaint)

    window.removeEventListener("beforeunload", this._onStart)
    window.removeEventListener("pagehide",     this._onStart)
    window.removeEventListener("pageshow",     this._onPageShow)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)

    if (this._clickBound)  { document.removeEventListener("click",  this._onPrimeLink, { capture: true });  this._clickBound  = false }
    if (this._submitBound) { document.removeEventListener("submit", this._onPrimeForm, { capture: true });  this._submitBound = false }

    this._clearTimer()
    this._stopBar()
  }

  // ---------- Prime early on interactions ----------
  _onPrimeLink(e) {
    if (e.button !== 0) return
    if (this.element.contains(e.target)) return

    const a = e.target?.closest?.("a[href]"); if (!a) return
    const href = a.getAttribute("href") || ""
    if (a.hasAttribute("data-no-overlay")) return
    if (a.target && a.target !== "_self") return
    if (a.hasAttribute("download")) return
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return
    if (a.origin && a.origin !== location.origin) return
    if (!href || href === "#" || href.startsWith("#") || href.startsWith("javascript:")) return
    if (this._showing) { e.preventDefault(); return }

    // Set dynamic label from the link we’re about to open
    this._setLabel(this._guessLabelFromLink(a))

    const turbo = a.getAttribute("data-turbo")
    if (turbo === "false") {
      e.preventDefault()
      if (this._navArmed) return
      this._navArmed = true
      this._networkStarted = true
      this._show()
      setTimeout(() => { location.href = a.href }, 60) // paint then hard-nav
    } else {
      this._networkStarted = true
      this._show()
    }
  }

  _onPrimeForm(e) {
    if (this.element.contains(e.target)) return
    const form = e.target?.closest?.("form"); if (!form || form.hasAttribute("data-no-overlay")) return
    if (this._showing) { e.preventDefault(); return }

    // Use submit button label, aria-label, or form dataset as the destination label
    const btn = e.submitter
    const lbl = btn?.getAttribute?.("aria-label") || btn?.value || form?.dataset?.label || "SUBMIT"
    this._setLabel(this._formatLabel(lbl))

    this._networkStarted = true
    this._show()
  }

  // ---------- Start / End ----------
  _onStart(e) {
    const form = e?.target?.closest?.("form") || e?.detail?.formSubmission?.formElement
    if (form?.hasAttribute?.("data-no-overlay")) return

    if (this._consumeSkipNextOverlay()) return

    // For programmatic visits (e.g. Turbo.visit), set a best-effort label from URL
    if (e?.detail?.url && !this._showing) {
      try {
        const url = new URL(e.detail.url)
        const raw = decodeURIComponent(url.pathname.split("/").filter(Boolean).pop() || url.host)
        this._setLabel(this._formatLabel(raw))
      } catch (_) {}
    }

    this._networkStarted = true
    if (this._showing || this._timer) return
    this._timer = setTimeout(() => { this._timer = null; this._show() }, this.delayMs)
  }

  // Hide only AFTER the new DOM paints (double RAF)
  _onEndAfterPaint() {
    this._clearTimer()
    this._navArmed = false

    if (!this._showing) { this._resetVisualState(); this._networkStarted = false; return }
    if (!this._pageTokenAdvanced()) return

    this._finishBar(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._hide()
          this._networkStarted = false
        })
      })
    })
  }

  _onPageShow()        { this._resetVisualState() }
  _onVisibilityChange(){ if (document.visibilityState === "hidden") this._resetVisualState() }

  _clearTimer() {
    if (this._timer)           { clearTimeout(this._timer);           this._timer = null }
    if (this._failsafe)        { clearTimeout(this._failsafe);        this._failsafe = null }
    if (this._noNavAbortTimer) { clearTimeout(this._noNavAbortTimer); this._noNavAbortTimer = null }
  }

  _consumeSkipNextOverlay() {
    const root = document.documentElement
    if (root?.dataset?.skipNextOverlay !== "true") return false

    delete root.dataset.skipNextOverlay
    this._clearTimer()
    this._resetVisualState()
    return true
  }

  // ---------- Internals ----------
  _show() {
    if (this._showing) return
    this._showing   = true
    this._progress  = 0
    this._startTime = performance.now()
    this._shownPageToken = document.body?.dataset?.pageToken || null

    if (!this.hasBackdropTarget || !this.hasTagTarget) return

    // Container catches clicks
    this.element.style.pointerEvents = "auto"
    this.element.classList.remove("pointer-events-none")
    this.element.classList.add("pointer-events-auto")

    // Backdrop visible immediately (inline ensures opacity even if classes are purged)
    this.backdropTarget.classList.remove("hidden", "pointer-events-none")
    this.backdropTarget.classList.add("opacity-100")
    this.backdropTarget.style.opacity = "1"
    this.backdropTarget.style.backgroundColor = "rgba(0,0,0,0.82)"
    Object.assign(this.backdropTarget.style, { position: "fixed", top: "0", left: "0", right: "0", bottom: "0" })

    // Tag visible
    this.tagTarget.classList.remove("hidden", "pointer-events-none")
    this.tagTarget.classList.add("opacity-100")
    this.tagTarget.style.opacity = "1"
    this.tagTarget.style.display = "grid"
    this.tagTarget.style.visibility = "visible"
    this.tagTarget.style.pointerEvents = "none"

    // Ensure label has something (fallback)
    if (this.hasLabelTarget && !this.labelTarget.textContent.trim()) {
      this._setLabel(this._formatLabel(document.title || "LOADING"))
    }

    // Optional blur
    if (CSS.supports?.("backdrop-filter: blur(1px)")) {
      this.backdropTarget.classList.add("backdrop-blur-sm")
    }

    // Next paint: backdrop intercepts clicks
    requestAnimationFrame(() => {
      this.backdropTarget.classList.add("pointer-events-auto")
      this.backdropTarget.classList.remove("pointer-events-none")
    })

    // Keep the pigeon console as the primary visual. The progress rail is only
    // shown for reduced-motion fallback and stays centered under the console.
    if (this.hasBarTarget) {
      this._normalizeBarLayout()
      this.barTarget.classList.add("pointer-events-none")
      if (this._showCenteredBar) {
        this.barTarget.classList.remove("hidden")
        this.barTarget.classList.add("pinball-loading-progress-visible")
      } else {
        this.barTarget.classList.add("hidden")
        this.barTarget.classList.remove("pinball-loading-progress-visible")
      }
    }

    // Animate toward 95% with easing that slows over time (so it doesn’t finish early)
    this._setBarWidth(5)
    this._startBar()

    // Minimum visible time + safeties
    this._minShowUntil = performance.now() + 1850

    // If no real nav starts (weird clicks), auto-hide
    this._noNavAbortTimer = setTimeout(() => { if (this._showing && !this._networkStarted) this._hide() }, 300)

    // Hard failsafe
    this._failsafe = setTimeout(() => { if (this._showing) this._resetVisualState() }, 12000)
  }

  _hide() {
    // Stop catching clicks immediately
    this.backdropTarget.classList.remove("pointer-events-auto")
    this.backdropTarget.classList.add("pointer-events-none")

    this.element.classList.remove("pointer-events-auto")
    this.element.classList.add("pointer-events-none")
    this.element.style.pointerEvents = "none"

    // Fade out (class + inline for certainty)
    this.backdropTarget.classList.add("opacity-0")
    this.backdropTarget.style.opacity = "0"
    this.tagTarget.classList.add("opacity-0")
    this.tagTarget.style.opacity = "0"

    setTimeout(() => this._resetVisualState(), 200)

    this._showing = false
    this._clearTimer()
  }

  _resetVisualState() {
    this.element.classList.remove("pointer-events-auto")
    this.element.classList.add("pointer-events-none")
    this.element.style.pointerEvents = "none"

    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("backdrop-blur-sm", "pointer-events-auto")
      this.backdropTarget.classList.add("hidden", "opacity-0", "pointer-events-none")
      this.backdropTarget.style.opacity = "0"
      Object.assign(this.backdropTarget.style, { position: "fixed", top: "0", left: "0", right: "0", bottom: "0", backgroundColor: "rgba(0,0,0,0.82)" })
    }
    if (this.hasTagTarget) {
      this.tagTarget.classList.add("hidden", "opacity-0", "pointer-events-none")
      this.tagTarget.style.opacity = "0"
      this.tagTarget.style.removeProperty("display")
      this.tagTarget.style.removeProperty("visibility")
      this.tagTarget.style.removeProperty("pointer-events")
    }
    if (this.hasBarTarget) {
      this._normalizeBarLayout()
      this._setBarWidth(0)
      this.barTarget.setAttribute("aria-valuenow", "0")
      this.barTarget.classList.add("hidden", "pointer-events-none")
      this.barTarget.classList.remove("pinball-loading-progress-visible")
    }
    this._stopBar()
    this._clearTimer()
    this._showing = false
    this._navArmed = false
    this._networkStarted = false
    this._shownPageToken = null
  }

  // ---------- Progress logic ----------
  _startBar() {
    if (!this.hasBarTarget || this.disableBarValue) return
    this._stopBar()
    this._tick = setInterval(() => {
      const elapsed = Math.max(0, performance.now() - this._startTime) // ms
      // Ease: fast early, slow late; cap at 95%
      // progress target curve ~ 1 - e^{-t/τ}, with a slower τ so
      // the bar lingers longer instead of sprinting to the end.
      const tau = 2800
      const target = 95 * (1 - Math.exp(-elapsed / tau))
      const next = Math.max(this._progress, Math.min(target, 95))
      this._progress = next
      this._setBarWidth(this._progress)
    }, 100)
  }

  _finishBar(done) {
    if (!this.hasBarTarget || this.disableBarValue) {
      const wait = Math.max(0, (this._minShowUntil || 0) - performance.now())
      setTimeout(() => done && done(), 320 + wait)
      return
    }
    this._stopBar()
    this._progress = 100
    this._setBarWidth(100)
    const wait = Math.max(0, (this._minShowUntil || 0) - performance.now())
    setTimeout(() => done && done(), 320 + wait)
  }

  _stopBar() {
    if (this._tick) { clearInterval(this._tick); this._tick = null }
  }

  _pageTokenAdvanced() {
    const current = document.body?.dataset?.pageToken || null
    return !this._shownPageToken || !current || current !== this._shownPageToken
  }

  _setBarWidth(pct) {
    const clamped = Math.max(0, Math.min(100, pct))
    if (this.hasBarTarget) {
      this.barTarget.style.setProperty("--loading-progress", `${clamped}%`)
      this.barTarget.setAttribute("aria-valuenow", String(Math.round(clamped)))
      this.barTarget.setAttribute("aria-valuetext", `${Math.round(clamped)}%`)
    }
  }

  _normalizeBarLayout() {
    if (!this.hasBarTarget) return

    this.barTarget.style.removeProperty("width")
    this.barTarget.style.removeProperty("height")
    this.barTarget.style.removeProperty("left")
    this.barTarget.style.removeProperty("top")
    this.barTarget.style.removeProperty("right")
    this.barTarget.style.removeProperty("bottom")
    this.barTarget.style.removeProperty("transform")
    this.barTarget.style.removeProperty("background")
    this.barTarget.style.removeProperty("position")
  }

  // ---------- Label helpers ----------
  _setLabel(text) {
    if (!this.hasLabelTarget) return
    const clean = this._formatLabel(text)
    this.labelTargets.forEach((target) => {
      target.textContent = clean || "LOADING"
    })
  }

  _guessLabelFromLink(a) {
    const ds = a.dataset?.label
    const aria = a.getAttribute("aria-label")
    const title = a.getAttribute("title")
    const txt = (a.textContent || "").trim()
    const fromText = txt && !/^\s*$/.test(txt) ? txt : ""
    const fromUrl = (() => {
      try {
        const p = a.pathname || new URL(a.href).pathname
        const last = decodeURIComponent(p.split("/").filter(Boolean).pop() || "")
        return last || a.host
      } catch { return "" }
    })()
    return ds || aria || title || fromText || fromUrl || a.href
  }

  _formatLabel(raw) {
    const s = (raw || "").replace(/\s+/g, " ").trim()
    if (!s) return ""
    const pretty = s.replace(/[-_]+/g, " ").toUpperCase()
    return pretty.length > 60 ? pretty.slice(0, 57).trimEnd() + "…" : pretty
  }
}
